import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Cake } from "lucide-react";

// Frontend-only mock chat. Will be wired to Claude (Emergent LLM key) in backend phase.
const SUGGESTIONS = [
  "Help me pick a birthday cake",
  "Do you have eggless options?",
  "What's your delivery time?",
];

const mockReply = (text) => {
  const t = text.toLowerCase();
  if (t.includes("eggless")) return "Yes! Every cake at Globaltreats has a 100% eggless option \u2014 just choose it while ordering. \ud83c\udf70";
  if (t.includes("deliver") || t.includes("area") || t.includes("location")) return "We deliver from the next day onwards (you can schedule up to a month ahead) across Noida, Greater Noida & Indirapuram (Ghaziabad) \u2014 within 20 km of Sector 78, Noida. Delivery is free on orders above \u20B9999!";
  if (t.includes("birthday")) return "Our bestselling birthday cakes are the Happy Birthday Rose Cake and Pink Drip Celebration Cake. Want me to suggest one by flavour or budget?";
  return "Thanks for reaching out to Globaltreats! Once our AI assistant is connected, I'll give you personalised cake recommendations. For now, browse our Bestsellers or ask about flavours, delivery and eggless options. \ud83e\uddc1";
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm Swati, your Globaltreats assistant. \ud83c\udf82 How can I help you find the perfect cake today?" },
  ]);
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const send = (text) => {
    const content = (text ?? input).trim();
    if (!content) return;
    setMessages((m) => [...m, { role: "user", text: content }]);
    setInput("");
    setTyping(true);
    setTimeout(() => {
      setMessages((m) => [...m, { role: "assistant", text: mockReply(content) }]);
      setTyping(false);
    }, 800);
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-[80] w-16 h-16 rounded-full bg-[#D46A8A] text-white grid place-items-center bakery-shadow-lg hover:scale-105 transition-transform floaty"
        aria-label="Chat with us"
      >
        {open ? <X size={26} /> : <MessageCircle size={28} />}
      </button>

      <div
        className={`fixed bottom-24 right-5 z-[80] w-[92vw] max-w-sm h-[520px] max-h-[75vh] bg-white rounded-3xl bakery-shadow-lg overflow-hidden flex flex-col transition-all duration-300 origin-bottom-right ${open ? "scale-100 opacity-100" : "scale-90 opacity-0 pointer-events-none"}`}
      >
        <div className="bg-[#6B4226] text-white px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#D46A8A] grid place-items-center"><Cake size={20} /></div>
          <div>
            <div className="font-display font-semibold">Swati · Globaltreats</div>
            <div className="text-xs text-[#E8A6B8] flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span> Online now</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FFF8F0]">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[#D46A8A] text-white rounded-br-sm" : "bg-white text-[#3A2417] rounded-bl-sm bakery-shadow"}`}>
                {m.text}
              </div>
            </div>
          ))}
          {typing && (
            <div className="flex justify-start">
              <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-sm bakery-shadow flex gap-1">
                {[0, 1, 2].map((d) => (
                  <span key={d} className="w-2 h-2 rounded-full bg-[#E8A6B8] animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                ))}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {messages.length <= 1 && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} className="text-xs bg-[#F6E7DA] text-[#6B4226] px-3 py-1.5 rounded-full hover:bg-[#EADBCB] transition-colors">
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="p-3 border-t border-[#EADBCB] flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type your message..."
            className="flex-1 h-11 px-4 rounded-full bg-[#F6E7DA] text-sm outline-none focus:ring-2 focus:ring-[#E8A6B8]"
          />
          <button onClick={() => send()} className="w-11 h-11 rounded-full bg-[#D46A8A] text-white grid place-items-center hover:bg-[#c25a7b] transition-colors">
            <Send size={18} />
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
